import clsx from 'clsx';
import React, { useContext } from 'react';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { UserContext } from '@eas/common-web';
import { Me } from '@models';
import { Role } from '@enums';
import { Search } from './search/search';
import {
  DashboardSearchContext,
  DashboardSearchContextProvider,
} from './search/search-dashboard-context';
import { DashboardSearchResults } from './search/search-results';
import { AssistantMyEsFilesStatistics } from './statistics/assistant-my-es-files';
import { DocumentsStatistics } from './statistics/documents';
import { EsFilesStatistics } from './statistics/es-files';
import { JudgeMyEsFilesStatistics } from './statistics/judge-my-es-files';
import { JudgeMyTeamEsFilesStatistics } from './statistics/judge-my-team-es-files';
import { UserOverviewStatistics } from './statistics/user-overview/user-overview';

export const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginBottom: 0,
  },
  root: {
    minHeight: 'calc(100vh - 64px)',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f1f3f4',
    overflow: 'auto',
  },
  subtitle: {
    fontWeight: 600,
  },
  content: {
    marginTop: 50,
    marginBottom: 50,
    flex: '1 0 auto',
  },
  padded: {
    padding: 16,
    margin: 0,
  },
  input: {
    marginTop: 16,
    paddingLeft: 8,
    display: 'flex',
    alignItems: 'center',
    borderRadius: 4,
    backgroundColor: '#e7e7e7',
  },
  search: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  topLayout: {
    display: 'grid',
    rowGap: 24,
    columnGap: 24,
    marginBottom: 24,
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateAreas: `
      'search'
      'results'
      'esFiles'
      'userOverview'
    `,
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 3fr)',
      gridTemplateAreas: `
        'results search'
        'esFiles userOverview'
      `,
      alignItems: 'start',
    },
  },
  topLayoutWithoutResults: {
    gridTemplateAreas: `
      'search'
      'esFiles'
      'userOverview'
    `,
    [theme.breakpoints.up('md')]: {
      gridTemplateAreas: `
        'esFiles search'
        'esFiles userOverview'
      `,
    },
  },
  topLayoutWithJudgeResults: {
    [theme.breakpoints.up('md')]: {
      gridTemplateAreas: `
        'results search'
        'esFiles esFiles'
      `,
    },
  },
  searchSection: {
    gridArea: 'search',
  },
  resultsSection: {
    gridArea: 'results',
  },
  esFilesSection: {
    gridArea: 'esFiles',
  },
  userOverviewSection: {
    gridArea: 'userOverview',
    alignSelf: 'start',
    height: 'fit-content',
  },
  judgeSection: {
    marginTop: 24,
  },
}));

function DashboardContent({
  isJudge,
  isAssistant,
}: {
  isJudge: boolean | undefined;
  isAssistant: boolean;
}) {
  const classes = useStyles();
  const { wasSubmitted } = useContext(DashboardSearchContext);
  const { user } = useContext<UserContext<Me>>(UserContext);
  const activeSubstitution = user?.additionalDetails?.activeSubstitution;
  const effectiveUserId = activeSubstitution?.substitutedUser?.id ?? user?.id;
  const assistantStatisticsKey = `${effectiveUserId ?? ''}:${
    activeSubstitution?.id ?? ''
  }`;

  return (
    <>
      <div
        className={clsx(classes.topLayout, {
          [classes.topLayoutWithoutResults]: !wasSubmitted,
          [classes.topLayoutWithJudgeResults]: isJudge && wasSubmitted,
        })}
      >
        <div className={classes.searchSection}>
          <Search />
          <UserOverviewStatistics />
        </div>
        <DashboardSearchResults />
        <div className={classes.esFilesSection}>
          {(isJudge || isAssistant) && (
            <Typography
              variant="subtitle1"
              className={clsx(classes.padded, classes.subtitle)}
            >
              {isJudge ? 'Moje spisy' : 'Moje soudní spisy'}
            </Typography>
          )}
          {isJudge ? (
            <Grid container spacing={3}>
              <JudgeMyEsFilesStatistics />
            </Grid>
          ) : isAssistant ? (
            <Grid container spacing={3}>
              <AssistantMyEsFilesStatistics key={assistantStatisticsKey} />
            </Grid>
          ) : (
            <EsFilesStatistics />
          )}
        </div>
      </div>

      {isJudge ? (
        <div className={classes.judgeSection}>
          <Typography
            variant="subtitle1"
            className={clsx(classes.padded, classes.subtitle)}
          >
            Spisy mých asistentů
          </Typography>
          <Grid container spacing={3}>
            <JudgeMyTeamEsFilesStatistics />
            <Grid item xs={12}>
              <EsFilesStatistics />
            </Grid>
          </Grid>
        </div>
      ) : (
        <DocumentsStatistics />
      )}
    </>
  );
}

export function Dashboard() {
  const classes = useStyles();

  const { user } = useContext<UserContext<Me>>(UserContext);

  const isJudge = user?.userRoles.map((role) => role.role).includes(Role.JUDGE);
  const isAssistant =
    user?.userRoles.some((role) => role.role === Role.ASSISTANT) ?? false;

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <Container maxWidth="xl">
          <DashboardSearchContextProvider>
            <DashboardContent isJudge={isJudge} isAssistant={isAssistant} />
          </DashboardSearchContextProvider>
        </Container>
      </div>
    </div>
  );
}
